import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { Button } from '../ui/button';
import { 
    DropdownMenu, 
    DropdownMenuContent, 
    DropdownMenuItem, 
    DropdownMenuSeparator, 
    DropdownMenuTrigger 
} from '../ui/dropdown-menu';
import { ShoppingCart, User, LogOut, Package, LayoutDashboard, Users, Clock, Menu, X, ArrowLeftRight, Store, Settings, DollarSign, Smartphone, MapPin, Wallet } from 'lucide-react';
import { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const Navbar = () => {
    const { user, logout, isAdmin, isApproved, token } = useAuth();
    const { itemCount, formattedTime, hasItems, isExpiringSoon } = useCart();
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [creditBalance, setCreditBalance] = useState(0);

    const isAdminView = location.pathname.startsWith('/admin');

    // Fetch credit balance for approved users
    useEffect(() => {
        const fetchCredit = async () => {
            if (user && isApproved && token) {
                try {
                    const response = await axios.get(`${API_URL}/account/credit`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setCreditBalance(response.data.total_credit || 0);
                } catch (error) {
                    console.error('Error fetching credit:', error);
                }
            }
        };
        fetchCredit();
    }, [user, isApproved, token, location.pathname]);

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const toggleView = () => {
        if (isAdminView) {
            navigate('/catalog');
        } else {
            navigate('/admin');
        }
    };

    const isActive = (path) => location.pathname === path;

    return (
        <nav className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm" data-testid="navbar">
            {/* Timer bar */}
            {hasItems && isApproved && (
                <div className={`px-6 py-2.5 flex justify-between items-center text-sm ${
                    isExpiringSoon 
                        ? 'bg-gradient-to-r from-rose-50 to-red-50 border-b border-rose-200' 
                        : 'bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200'
                }`}>
                    <div className="flex items-center gap-2">
                        <Clock className={`w-4 h-4 ${isExpiringSoon ? 'text-rose-600 timer-warning' : 'text-amber-600'}`} />
                        <span className={isExpiringSoon ? 'text-rose-700 font-medium' : 'text-amber-700'}>
                            Réservation expire dans: <span className="font-mono font-bold">{formattedTime}</span>
                        </span>
                    </div>
                    <Link 
                        to="/cart" 
                        className="text-blue-600 hover:text-blue-800 font-semibold transition-colors" 
                        data-testid="timer-view-cart"
                    >
                        Voir le panier ({itemCount})
                    </Link>
                </div>
            )}

            <div className="container mx-auto px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    {/* Logo - PHONEB2B style badge - always links to home */}
                    <Link to="/" className="flex items-center" data-testid="logo">
                        <div className="bg-blue-600 rounded-xl p-1.5 shadow-md">
                            <div className="border-2 border-white rounded-lg px-5 py-1.5">
                                <span 
                                    className="font-black text-2xl text-white tracking-tight"
                                    style={{ fontFamily: 'var(--font-heading)' }}
                                >
                                    PHONEB2B
                                </span>
                            </div>
                        </div>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center gap-1">
                        {user && isApproved && !isAdminView && (
                            <>
                                <Link 
                                    to="/" 
                                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                        location.pathname === '/' 
                                            ? 'bg-blue-100 text-blue-700' 
                                            : 'text-gray-600 hover:text-blue-700 hover:bg-blue-50'
                                    }`}
                                    data-testid="nav-home"
                                >
                                    Accueil
                                </Link>
                                <Link 
                                    to="/catalog" 
                                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                        isActive('/catalog') 
                                            ? 'bg-blue-100 text-blue-700' 
                                            : 'text-gray-600 hover:text-blue-700 hover:bg-blue-50'
                                    }`}
                                    data-testid="nav-catalog"
                                >
                                    Catalogue
                                </Link>
                                <Link 
                                    to="/orders" 
                                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                        isActive('/orders') 
                                            ? 'bg-blue-100 text-blue-700' 
                                            : 'text-gray-600 hover:text-blue-700 hover:bg-blue-50'
                                    }`}
                                    data-testid="nav-orders"
                                >
                                    Mes Commandes
                                </Link>
                            </>
                        )}

                        {user && isAdmin && isAdminView && (
                            <>
                                <Link 
                                    to="/admin" 
                                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                        isActive('/admin') 
                                            ? 'bg-blue-100 text-blue-700' 
                                            : 'text-gray-600 hover:text-blue-700 hover:bg-blue-50'
                                    }`}
                                    data-testid="nav-admin-dashboard"
                                >
                                    Dashboard
                                </Link>
                                <Link 
                                    to="/admin/users" 
                                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                        isActive('/admin/users') 
                                            ? 'bg-blue-100 text-blue-700' 
                                            : 'text-gray-600 hover:text-blue-700 hover:bg-blue-50'
                                    }`}
                                    data-testid="nav-admin-users"
                                >
                                    Utilisateurs
                                </Link>
                                <Link 
                                    to="/admin/orders" 
                                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                        isActive('/admin/orders') 
                                            ? 'bg-blue-100 text-blue-700' 
                                            : 'text-gray-600 hover:text-blue-700 hover:bg-blue-50'
                                    }`}
                                    data-testid="nav-admin-orders"
                                >
                                    Commandes
                                </Link>
                                <Link 
                                    to="/admin/products" 
                                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                        isActive('/admin/products') 
                                            ? 'bg-blue-100 text-blue-700' 
                                            : 'text-gray-600 hover:text-blue-700 hover:bg-blue-50'
                                    }`}
                                    data-testid="nav-admin-products"
                                >
                                    Stock
                                </Link>
                                <Link 
                                    to="/admin/reservations" 
                                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                        isActive('/admin/reservations') 
                                            ? 'bg-blue-100 text-blue-700' 
                                            : 'text-gray-600 hover:text-blue-700 hover:bg-blue-50'
                                    }`}
                                    data-testid="nav-admin-reservations"
                                >
                                    Réservations
                                </Link>
                                <Link 
                                    to="/admin/pricing" 
                                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                        isActive('/admin/pricing') 
                                            ? 'bg-blue-100 text-blue-700' 
                                            : 'text-gray-600 hover:text-blue-700 hover:bg-blue-50'
                                    }`}
                                    data-testid="nav-admin-pricing"
                                >
                                    Pricing
                                </Link>
                            </>
                        )}
                    </div>

                    {/* Right side */}
                    <div className="flex items-center gap-3">
                        {/* Switch View Button for Admin */}
                        {user && isAdmin && (
                            <Button 
                                variant="outline"
                                onClick={toggleView}
                                className={`hidden sm:flex items-center gap-2 rounded-lg font-semibold ${
                                    isAdminView 
                                        ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100' 
                                        : 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100'
                                }`}
                                data-testid="switch-view-btn"
                            >
                                {isAdminView ? (
                                    <>
                                        <Store className="w-4 h-4" />
                                        Vue Client
                                    </>
                                ) : (
                                    <>
                                        <Settings className="w-4 h-4" />
                                        Vue Admin
                                    </>
                                )}
                            </Button>
                        )}

                        {user && isApproved && !isAdminView && (
                            <Link to="/cart" className="relative" data-testid="cart-button">
                                <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    className="relative rounded-lg hover:bg-blue-50"
                                >
                                    <ShoppingCart className="w-5 h-5 text-gray-600" />
                                    {itemCount > 0 && (
                                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-blue-600 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-sm">
                                            {itemCount}
                                        </span>
                                    )}
                                </Button>
                            </Link>
                        )}

                        {user ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" className="flex items-center gap-2 rounded-lg hover:bg-blue-50" data-testid="user-menu">
                                        <div className="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center border border-blue-200">
                                            <User className="w-4 h-4 text-blue-600" />
                                        </div>
                                        <span className="hidden sm:block text-sm font-medium max-w-32 truncate text-gray-700">
                                            {user.company_name}
                                        </span>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56 rounded-xl border-gray-100 shadow-lg">
                                    <div className="px-3 py-2">
                                        <p className="text-sm font-semibold text-gray-900">{user.contact_name}</p>
                                        <p className="text-xs text-gray-500">{user.email}</p>
                                        {isApproved && creditBalance > 0 && (
                                            <div className="flex items-center gap-1.5 mt-2 px-2 py-1.5 bg-green-50 border border-green-200 rounded-md">
                                                <Wallet className="w-4 h-4 text-green-600" />
                                                <span className="text-sm font-semibold text-green-700">
                                                    Crédit: {creditBalance.toFixed(2)} €
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <DropdownMenuSeparator />
                                    {isApproved && (
                                        <>
                                            <DropdownMenuItem 
                                                onClick={() => navigate('/orders')} 
                                                className="rounded-lg mx-1 cursor-pointer"
                                                data-testid="menu-orders"
                                            >
                                                <Package className="w-4 h-4 mr-2" />
                                                Mes Commandes
                                            </DropdownMenuItem>
                                            <DropdownMenuItem 
                                                onClick={() => navigate('/sav')} 
                                                className="rounded-lg mx-1 cursor-pointer"
                                                data-testid="menu-sav"
                                            >
                                                <ArrowLeftRight className="w-4 h-4 mr-2" />
                                                SAV / Retours
                                            </DropdownMenuItem>
                                            <DropdownMenuItem 
                                                onClick={() => navigate('/wallet')} 
                                                className="rounded-lg mx-1 cursor-pointer"
                                                data-testid="menu-wallet"
                                            >
                                                <Wallet className="w-4 h-4 mr-2" />
                                                Mon Wallet
                                                {creditBalance > 0 && (
                                                    <span className="ml-auto text-xs text-green-600 font-semibold">
                                                        {creditBalance.toFixed(2)} €
                                                    </span>
                                                )}
                                            </DropdownMenuItem>
                                            <DropdownMenuItem 
                                                onClick={() => navigate('/addresses')} 
                                                className="rounded-lg mx-1 cursor-pointer"
                                                data-testid="menu-addresses"
                                            >
                                                <MapPin className="w-4 h-4 mr-2" />
                                                Mes Adresses
                                            </DropdownMenuItem>
                                            <DropdownMenuSeparator />
                                        </>
                                    )}
                                    {isAdmin && (
                                        <>
                                            <DropdownMenuItem 
                                                onClick={() => navigate('/admin')} 
                                                className="rounded-lg mx-1 cursor-pointer"
                                                data-testid="menu-admin"
                                            >
                                                <LayoutDashboard className="w-4 h-4 mr-2" />
                                                Administration
                                            </DropdownMenuItem>
                                            <DropdownMenuSeparator />
                                        </>
                                    )}
                                    <DropdownMenuItem 
                                        onClick={handleLogout} 
                                        className="text-rose-600 rounded-lg mx-1 cursor-pointer hover:bg-rose-50 hover:text-rose-700"
                                        data-testid="menu-logout"
                                    >
                                        <LogOut className="w-4 h-4 mr-2" />
                                        Déconnexion
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <div className="flex items-center gap-2">
                                <Link to="/login">
                                    <Button 
                                        variant="ghost" 
                                        className="rounded-lg text-blue-600 hover:bg-blue-50"
                                        data-testid="login-button"
                                    >
                                        Connexion
                                    </Button>
                                </Link>
                                <Link to="/register">
                                    <Button 
                                        className="rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700"
                                        data-testid="register-button"
                                    >
                                        Ouvrir un compte
                                    </Button>
                                </Link>
                            </div>
                        )}

                        {/* Mobile menu button */}
                        <Button 
                            variant="ghost" 
                            size="icon" 
                            className="md:hidden rounded-lg hover:bg-blue-50"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            data-testid="mobile-menu-toggle"
                        >
                            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </Button>
                    </div>
                </div>

                {/* Mobile menu */}
                {mobileMenuOpen && (
                    <div className="md:hidden border-t border-gray-100 py-4">
                        {user && isAdmin && (
                            <button
                                className="w-full flex items-center gap-2 px-4 py-3 mb-2 bg-blue-50 text-blue-700 rounded-lg font-semibold"
                                onClick={() => { toggleView(); setMobileMenuOpen(false); }}
                            >
                                <ArrowLeftRight className="w-4 h-4" />
                                {isAdminView ? 'Passer en Vue Client' : 'Passer en Vue Admin'}
                            </button>
                        )}

                        {user && isApproved && !isAdminView && (
                            <div className="space-y-1">
                                <Link 
                                    to="/" 
                                    className="block px-4 py-2.5 hover:bg-blue-50 rounded-lg font-medium text-gray-700"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    Accueil
                                </Link>
                                <Link 
                                    to="/catalog" 
                                    className="block px-4 py-2.5 hover:bg-blue-50 rounded-lg font-medium text-gray-700"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    Catalogue
                                </Link>
                                <Link 
                                    to="/orders" 
                                    className="block px-4 py-2.5 hover:bg-blue-50 rounded-lg font-medium text-gray-700"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    Mes Commandes
                                </Link>
                                <Link 
                                    to="/addresses" 
                                    className="block px-4 py-2.5 hover:bg-blue-50 rounded-lg font-medium text-gray-700"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    Mes Adresses
                                </Link>
                            </div>
                        )}

                        {user && isAdmin && isAdminView && (
                            <div className="space-y-1">
                                <Link to="/admin" className="block px-4 py-2.5 hover:bg-blue-50 rounded-lg font-medium text-gray-700" onClick={() => setMobileMenuOpen(false)}>Dashboard</Link>
                                <Link to="/admin/users" className="block px-4 py-2.5 hover:bg-blue-50 rounded-lg font-medium text-gray-700" onClick={() => setMobileMenuOpen(false)}>Utilisateurs</Link>
                                <Link to="/admin/orders" className="block px-4 py-2.5 hover:bg-blue-50 rounded-lg font-medium text-gray-700" onClick={() => setMobileMenuOpen(false)}>Commandes</Link>
                                <Link to="/admin/products" className="block px-4 py-2.5 hover:bg-blue-50 rounded-lg font-medium text-gray-700" onClick={() => setMobileMenuOpen(false)}>Stock</Link>
                                <Link to="/admin/reservations" className="block px-4 py-2.5 hover:bg-blue-50 rounded-lg font-medium text-gray-700" onClick={() => setMobileMenuOpen(false)}>Réservations</Link>
                                <Link to="/admin/pricing" className="block px-4 py-2.5 hover:bg-blue-50 rounded-lg font-medium text-gray-700" onClick={() => setMobileMenuOpen(false)}>Pricing</Link>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </nav>
    );
};

export default Navbar;
